import type {
    LOAN_REQUEST_OFFER_SOURCE,
    LOANREQUEST_BUCKET,
    LOANREQUEST_BUCKETS_FILTER,
    LOANREQUEST_STATUS,
    SUMMARY_ROUTE,
} from 'cpool-constants';
import {
    AGGREGATE_NAMES,
    API_ROUTES,
    CHAT_RESOLVERS,
    CORPORATION_RESOLVERS,
    LOAN_REQUEST_RESOLVERS,
    LOANREQUEST_DEFAULT_STATUS,
    PARTNER_RESOLVERS,
    SUMMARY_ROUTES,
    TEMPLATE_RESOLVERS,
    USER_RESOLVERS,
    WABA_RESOLVERS,
} from 'cpool-constants';
import type { Schema_OfferFilter, StatusMailPreviewRequest } from 'cpool-schemas';
import type {
    LoanRequestResolver_GetSummary,
    SchemaUserResolver_GetAclPermissionsArgs,
} from 'cpool-tables/backend-resolvers';

import { PAGINATION } from '~/constants';
import type { HistoryEventsCategory } from '~/features/leadDetails/components/History/eventCategories.schema';

export const queryKeys = {
    duplicateLeads: (loanRequestId: string) =>
        [AGGREGATE_NAMES.LOAN_REQUEST, 'getDuplicateInfoByLead', { id: loanRequestId }] as const,
    find: () => [AGGREGATE_NAMES.LOAN_REQUEST, 'find', {}] as const,
    get: (id: string) => [AGGREGATE_NAMES.LOAN_REQUEST, 'get', { id }] as const,
    getAcceptedOfferCounts: (loanRequestId: string) =>
        [AGGREGATE_NAMES.LOAN_REQUEST, 'getAcceptedOfferCounts', { id: loanRequestId }] as const,
    getAcceptedOffers: (loanRequestId: string, filter?: Schema_OfferFilter) =>
        [AGGREGATE_NAMES.LOAN_REQUEST, 'getAcceptedOffers', { filter: filter || {}, id: loanRequestId }] as const,
    getAcknowledgedDocuments: (id: string) =>
        [AGGREGATE_NAMES.LOAN_REQUEST, 'getAcknowledgedDocuments', { id }] as const,
    getAclPermissions: (args?: SchemaUserResolver_GetAclPermissionsArgs) =>
        [
            AGGREGATE_NAMES.USER,
            'getAclPermissions',
            {
                ...(args?.loanRequestId && { loanRequestId: args.loanRequestId }),
                ...(args?.userId && { userId: args.userId }),
                ...(args?.chatLinkInfo && { chatLinkInfo: args.chatLinkInfo }),
            },
        ] as const,
    // position-store + invalidation prefix key for the notification bell
    getActiveUserSetLeads: () =>
        [AGGREGATE_NAMES.LOAN_REQUEST, LOAN_REQUEST_RESOLVERS.getActiveUserSetLeads, {}] as const,
    // position-store + invalidation prefix key for the notification bell
    getAddedAssignedUserLeads: () =>
        [AGGREGATE_NAMES.LOAN_REQUEST, LOAN_REQUEST_RESOLVERS.getAddedAssignedUserLeads, {}] as const,
    getAdvisersByTeam: (teamLeaderId: string | undefined) =>
        [AGGREGATE_NAMES.USER, USER_RESOLVERS.getAdvisersByTeam, { teamLeaderId }] as const,
    getAdvisersWithoutTeam: (teamLeaderId?: string | undefined, organization?: string | undefined) =>
        [AGGREGATE_NAMES.USER, 'getAdvisersWithoutTeam', { organization, teamLeaderId }] as const,
    getAgreements: (id: string) => [AGGREGATE_NAMES.LOAN_REQUEST, 'getAgreements', { id }] as const,
    getAllTemplates: () => [AGGREGATE_NAMES.TEMPLATE, 'getAllTemplates', {}] as const,
    getApplicantNotes: (applicantIds: Array<string>, id: string, changeType: string) =>
        [AGGREGATE_NAMES.LOAN_REQUEST, 'getApplicantNotes', { applicantIds, changeType, id }] as const,
    getApplicantNotesByLeadId: (id: string) => [AGGREGATE_NAMES.LOAN_REQUEST, 'getApplicantNotes', { id }] as const,
    getBankOffersByLeadId: (id: string) => [AGGREGATE_NAMES.LOAN_REQUEST, 'getBankOffersByLeadId', { id }],
    getBanks: () => [AGGREGATE_NAMES.LOAN_REQUEST, 'getBanks'] as const,
    getBucketConfigurations: () => [AGGREGATE_NAMES.SYSTEM_VARIABLE, 'getBucketConfigurations', {}] as const,
    getBucketHistory: (id?: string) => [AGGREGATE_NAMES.LOAN_REQUEST, 'getBucketHistory', { id }] as const,
    getBuckets: (id: string) => [AGGREGATE_NAMES.USER, 'getBuckets', { id }] as const,
    // Partial key (no `limit`/`before`/`after`) - matches every cursor-paged variant fetched for this
    // chat, since TanStack's default partial-match invalidation treats this as a subset of
    // the full searchParams object each page's query key actually carries.
    getChatMessages: (chatId: string) => [AGGREGATE_NAMES.CHAT, CHAT_RESOLVERS.getMessages, { chatId }] as const,
    getChats: () => [AGGREGATE_NAMES.CHAT, CHAT_RESOLVERS.getChats, {}] as const,
    getConsorsConsolidationData: (loanRequestId: string) =>
        [AGGREGATE_NAMES.LOAN_REQUEST, 'getConsorsConsolidationData', { id: loanRequestId }] as const,
    getConsorsNotesByLeadId: (id: string) => [AGGREGATE_NAMES.LOAN_REQUEST, 'getConsorsNotesByLeadId', { id }] as const,
    getConsorsSubscriptionStatus: (loanRequestId: string) =>
        [AGGREGATE_NAMES.LOAN_REQUEST, 'getConsorsSubscriptionStatus', { id: loanRequestId }] as const,
    getCorporationByDirectorId: (directorId: string | undefined) =>
        [AGGREGATE_NAMES.CORPORATION, 'getCorporationByDirectorId', { directorId }] as const,
    getCorporations: () => [AGGREGATE_NAMES.CORPORATION, 'list', {}] as const,
    getCounts: ({
        filters,
        route,
        status,
    }: {
        filters?: { tag?: Array<string>; bank?: Array<string>; advisers?: Array<string>; resubmission?: boolean };
        route?: SUMMARY_ROUTE;
        status?: LOANREQUEST_STATUS | LOANREQUEST_DEFAULT_STATUS;
    }) => [AGGREGATE_NAMES.LOAN_REQUEST, 'getCounts', { filters, route, status }] as const,
    getCreditRatingRequestedStatus: (id: string) =>
        [AGGREGATE_NAMES.LOAN_REQUEST, 'getCreditRatingRequestedStatus', { id }] as const,
    getDelegateId: (id: string) => [AGGREGATE_NAMES.USER, 'getDelegateId', { id }] as const,
    getDemandedDocuments: (id: string) => [AGGREGATE_NAMES.LOAN_REQUEST, 'getDemandedDocuments', { id }] as const,
    getDemandedDocumentsTemplate: (position?: number) =>
        [AGGREGATE_NAMES.TEMPLATE, 'getDemandedDocumentsTemplate', { position }] as const,
    getDivisionsByUserId: () => [AGGREGATE_NAMES.USER, USER_RESOLVERS.getDivisionsByUserId, {}] as const,
    getEuropaceEventRequestStatus: (id: string) =>
        [AGGREGATE_NAMES.LOAN_REQUEST, 'getEuropaceEventRequestStatus', { id }] as const,
    getEuropaceProcessEvents: (id: string) =>
        [AGGREGATE_NAMES.LOAN_REQUEST, 'getEuropaceProcessEvents', { id }] as const,
    // position-store key, not a cache key
    getFooters: () => [AGGREGATE_NAMES.TEMPLATE, TEMPLATE_RESOLVERS.getFooters] as const,
    // position-store key, not a cache key
    getHeaders: () => [AGGREGATE_NAMES.TEMPLATE, TEMPLATE_RESOLVERS.getHeaders] as const,
    // `userId` scopes the cache per authenticated user — unread counts are per-user read cursors,
    // so a shared key caused stale badge totals after impersonation/login-as-a-different-user.
    getInboxUnreadSummary: (userId: string) =>
        [AGGREGATE_NAMES.CHAT, CHAT_RESOLVERS.getInboxUnreadSummary, { userId }] as const,
    getLeadTrackStatus: (id: string) => [AGGREGATE_NAMES.USER, 'getLeadTrackStatus', { id }] as const,
    getLoanRequestNotes: (id: string) => ['loan-request', 'getNotes', { id }] as const,
    getLoanRequestStatusData: (id: string) =>
        [AGGREGATE_NAMES.LOAN_REQUEST, 'getLoanRequestStatusData', { id }] as const,
    getLoanRequestsWithUnverifiedDocuments: () =>
        [AGGREGATE_NAMES.LOAN_REQUEST, 'getLoanRequestsWithUnverifiedDocuments', {}] as const,
    getOfferAcceptanceStatusByOfferId: ({
        loanRequestId,
        offerId,
        source,
    }: {
        offerId: string;
        loanRequestId: string;
        source: LOAN_REQUEST_OFFER_SOURCE;
    }) =>
        [
            AGGREGATE_NAMES.LOAN_REQUEST,
            'getOfferAcceptanceStatusByOfferId',
            { id: loanRequestId, offerId, source },
        ] as const,
    getOfferById: (leadId: string, offerId: string, position?: number) =>
        [AGGREGATE_NAMES.LOAN_REQUEST, 'getOfferById', { id: leadId, offerId, position }] as const,
    getOfferDocumentsUploadData: (id: string) =>
        [AGGREGATE_NAMES.LOAN_REQUEST, 'getOfferDocumentsUploadData', { id }] as const,
    // TODO: Extend with other sources in the future
    getOfferRequestConditionsLatest: (id: string) =>
        [AGGREGATE_NAMES.LOAN_REQUEST, 'getOfferRequestConditionsLatest', { id }] as const,
    getOfferRequestState: (id: string, requestId: string | undefined) => [
        AGGREGATE_NAMES.LOAN_REQUEST,
        'getOfferRequestState',
        { id, requestId },
    ],
    getOffersAcceptanceStatusByLoanRequestId: ({
        loanRequestId,
        source,
    }: {
        loanRequestId: string;
        source: LOAN_REQUEST_OFFER_SOURCE;
    }) =>
        [
            AGGREGATE_NAMES.LOAN_REQUEST,
            'getOffersAcceptanceStatusByLoanRequestId',
            { id: loanRequestId, source },
        ] as const,
    getOffersByRequestId: (id: string, requestId?: string) =>
        [AGGREGATE_NAMES.LOAN_REQUEST, 'getOffersByRequestId', { id, requestId }] as const,
    getOwnCorporation: () => [AGGREGATE_NAMES.CORPORATION, CORPORATION_RESOLVERS.getOwnCorporation, {}] as const,
    getPartner: (id: string | undefined) => [AGGREGATE_NAMES.PARTNER, PARTNER_RESOLVERS.getPartner, { id }] as const,
    getPartners: (args: Record<string, unknown>) =>
        [AGGREGATE_NAMES.PARTNER, PARTNER_RESOLVERS.getPartners, args] as const,
    getPartnersCount: (args: Record<string, unknown>) =>
        [AGGREGATE_NAMES.PARTNER, PARTNER_RESOLVERS.getPartnersCount, args] as const,
    getPartnerSyncStatus: () => [AGGREGATE_NAMES.PARTNER, API_ROUTES.partnerSyncStatus, {}] as const,
    getPc24CreditImportStatus: (loanRequestId: string) =>
        [AGGREGATE_NAMES.LOAN_REQUEST, LOAN_REQUEST_RESOLVERS.getPc24CreditImportStatus, { loanRequestId }] as const,
    // position-store + invalidation prefix key for the notification bell
    getProcessedNotifications: () =>
        [AGGREGATE_NAMES.LOAN_REQUEST, LOAN_REQUEST_RESOLVERS.getProcessedNotifications, {}] as const,
    getRecordedDocuments: (id: string) => [AGGREGATE_NAMES.LOAN_REQUEST, 'getRecordedDocuments', { id }] as const,
    // position-store + invalidation prefix key — `{}` partially matches every date range
    getResubmissionLoanRequestsByDate: () =>
        [AGGREGATE_NAMES.LOAN_REQUEST, LOAN_REQUEST_RESOLVERS.getResubmissionLoanRequestsByDate, {}] as const,
    getSchufa: (id: string, applicantId?: string) =>
        [AGGREGATE_NAMES.LOAN_REQUEST, 'getSchufa', { ...(applicantId ? { applicantId } : {}), id }] as const,
    getSkagCreditApplication: (loanRequestId: string) =>
        [AGGREGATE_NAMES.LOAN_REQUEST, LOAN_REQUEST_RESOLVERS.getSkagCreditApplication, { loanRequestId }] as const,
    getStatusMailPreview: (leadId: string, request: StatusMailPreviewRequest | null) =>
        [AGGREGATE_NAMES.LOAN_REQUEST, API_ROUTES.previewStatusMail, { id: leadId, request }] as const,
    getSummary: (params?: {
        permissionsActionFilter?: Parameters<LoanRequestResolver_GetSummary>[1]['permissionsActionFilter'];
        route?: SUMMARY_ROUTE;
        limit?: number;
        offset?: number;
        status?: LOANREQUEST_STATUS | LOANREQUEST_DEFAULT_STATUS;
        filters?: {
            tag?: Array<string>;
            bank?: Array<string>;
            advisers?: Array<string>;
            resubmission?: boolean;
            bucket?: Array<LOANREQUEST_BUCKET | LOANREQUEST_BUCKETS_FILTER>;
        };
    }) =>
        params
            ? ([
                  AGGREGATE_NAMES.LOAN_REQUEST,
                  'getSummary',
                  {
                      filters: params.filters || {},
                      limit: params.limit ?? PAGINATION.LEAD_LIST.DEFAULT_PAGE_SIZE,
                      offset: params.offset ?? 0,
                      permissionsActionFilter: params.permissionsActionFilter,
                      route: params.route ?? SUMMARY_ROUTES.LEADS,
                      status: params.status ?? LOANREQUEST_DEFAULT_STATUS.ALL,
                  },
              ] as const)
            : ([AGGREGATE_NAMES.LOAN_REQUEST, 'getSummary'] as const),
    getSystemNotes: (id: string, position?: number | string | null) =>
        [
            AGGREGATE_NAMES.LOAN_REQUEST,
            LOAN_REQUEST_RESOLVERS.getSystemNotes,
            {
                id,
                ...(position != null ? { position } : {}),
            },
        ] as const,
    getTeamsByCorporation: (corporationId?: string | null) =>
        [AGGREGATE_NAMES.USER, 'getTeamsByCorporation', { corporationId }] as const,
    getTemplateById: (id: string) => [AGGREGATE_NAMES.TEMPLATE, 'getTemplateById', { id }] as const,
    // position-store + invalidation prefix key for the notification bell
    getUnreadConsorsNotes: () =>
        [AGGREGATE_NAMES.LOAN_REQUEST, LOAN_REQUEST_RESOLVERS.getUnreadConsorsNotes, {}] as const,
    getUser: (userId: string | undefined) => [AGGREGATE_NAMES.USER, 'get', { id: userId }] as const,
    getUserList: () => [AGGREGATE_NAMES.USER, 'list', {}] as const,
    getUsers: (args: Record<string, unknown>) => [AGGREGATE_NAMES.USER, 'getUsers', args] as const,
    getWhatsappPhoneNumbers: () => [AGGREGATE_NAMES.DIVISION, API_ROUTES.getWhatsappPhoneNumbers, {}] as const,
    getWhatsAppTemplates: () => [AGGREGATE_NAMES.WABA, WABA_RESOLVERS.getWhatsAppTemplates, {}] as const,
    historyEvents: (leadId: string, category: HistoryEventsCategory) => ['history-events', leadId, category] as const,
    listByDivision: (divisionId?: string | null) =>
        [AGGREGATE_NAMES.TEMPLATE, 'listByDivision', { divisionId }] as const,
    listByDivisions: () => [AGGREGATE_NAMES.TEMPLATE, 'listByDivisions', {}] as const,
    // position-store key, not a cache key
    listDivisionHeaderFooters: () =>
        [AGGREGATE_NAMES.TEMPLATE, TEMPLATE_RESOLVERS.listDivisionHeaderFooters, {}] as const,
    // position-store key for division (de)activations, not a cache key
    listDivisionHeaderFootersDivisionPosition: () =>
        [AGGREGATE_NAMES.TEMPLATE, TEMPLATE_RESOLVERS.listDivisionHeaderFooters, 'divisionPosition'] as const,
};

export type QueryKey = ReturnType<(typeof queryKeys)[keyof typeof queryKeys]>;
